import { useState, useCallback, useRef, useEffect } from 'react';

export interface SearchResult {
  id: string;
  name: string;
  slug: string;
  primaryImage: string | null;
  categoryName: string;
  startingPrice: number | null;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
const RECENT_SEARCHES_KEY = 'recent-searches';
const MAX_RECENT_SEARCHES = 5;
const DEBOUNCE_MS = 300;

export function useProductSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Load recent searches from localStorage
  const loadRecentSearches = useCallback(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch {
        setRecentSearches([]);
      }
    }
  }, []);

  // Search products via API
  const searchProducts = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/products/search?q=${encodeURIComponent(searchQuery)}&limit=8`
      );
      if (response.ok) {
        const data = await response.json();
        setResults(data.data || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced query change
  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        searchProducts(value);
      }, DEBOUNCE_MS);
    },
    [searchProducts]
  );

  // Save a search term to recent searches
  const saveRecentSearch = useCallback(
    (searchTerm: string) => {
      const updated = [
        searchTerm,
        ...recentSearches.filter((s) => s.toLowerCase() !== searchTerm.toLowerCase()),
      ].slice(0, MAX_RECENT_SEARCHES);

      setRecentSearches(updated);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    },
    [recentSearches]
  );

  // Clear all recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  }, []);

  // Reset state
  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    query,
    results,
    loading,
    recentSearches,
    handleQueryChange,
    saveRecentSearch,
    clearRecentSearches,
    loadRecentSearches,
    reset,
    setQuery,
    searchProducts,
  };
}
